import { useState } from 'react';
import cn from 'classnames';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import {
  BrainCircuit,
  Fingerprint,
  Merge,
  MessageCirclePlus,
  Mic,
  MoreHorizontal,
  Plus,
  Scissors,
  Trash2
} from 'lucide-react';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { useCaption } from '@utils/providers/caption-context.tsx';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { shortcutLabel } from '@utils/editors/shortcut-label.tsx';
import { useOpenTrainingDialog } from '@components/editors/caption-editor/body/caption-training-dialog.tsx';
import { useCommentsContext } from '@components/editors/shared/comments/comments-context.tsx';
import { useVoiceProfileFeature } from '@components/editors/shared/voice-profiles/use-voice-profile-feature.ts';
import { useVoiceIdentity } from '@components/editors/shared/voice-profiles/use-voice-identity.ts';
import { useVoiceSpeakerRow } from '@components/editors/shared/voice-profiles/use-voice-speaker-row.tsx';

type Props = {
  segment: DocumentSubtitleSegmentV1;
  filteredItems: DocumentSubtitleSegmentV1[];
  /**
   * #2250 — the tools stack shows different affordances per pane:
   *   - source / single mode: Plus, Split, kebab (count-changers) + Comment
   *   - translation pane in split view: Comment only. Count-changers are
   *     hidden because segment count belongs to the source.
   * `hideCountTools` is the flag; the caller (ScriptixCaptionEditor) sets
   * it when it sees a `sourceSegment` prop.
   */
  hideCountTools?: boolean;
};

export const CaptionSegmentEditingTools = ({
  segment,
  filteredItems,
  hideCountTools
}: Props) => {
  const {
    addListItem,
    removeListItem,
    splitListItem,
    joinListItems,
    session,
    documentResult
  } = useCaption();
  const { t } = useTranslation();
  const { getColour } = useThemeContext();
  const openTrainingDialog = useOpenTrainingDialog();

  // --- Voice profiles (#2355) -------------------------------------------
  // The caption layout has no speaker label to hang these off, and the issue
  // rules out a permanent extra button on every caption, so both actions live
  // in this segment's existing kebab menu. Captions carry no speaker name in
  // the document, so there is nothing to rewrite: the identity decision is
  // stored against the original diarization label instead, which is exactly
  // how the backend keeps it.
  const voiceSessionId = session?.session_id ?? '';
  const voiceDocumentId = documentResult?.result?.id ?? '';
  const voiceFeature = useVoiceProfileFeature({ session });
  // Fetched on demand unless a suggestion could be waiting; see the transcript
  // speaker menu for the same reasoning.
  const [voiceActivated, setVoiceActivated] = useState(false);
  const voiceIdentity = useVoiceIdentity(
    voiceSessionId,
    voiceDocumentId,
    !!voiceSessionId &&
      !!voiceDocumentId &&
      voiceFeature.canAssign &&
      (voiceFeature.eagerIdentity || voiceActivated)
  );
  const voiceRow = useVoiceSpeakerRow({
    sessionId: voiceSessionId,
    documentId: voiceDocumentId,
    startMs: segment.start,
    stopMs: segment.stop,
    currentName: segment.speaker ?? '',
    feature: voiceFeature,
    identity: voiceIdentity,
    onActivate: () => setVoiceActivated(true)
  });

  const currentIndex = filteredItems.findIndex(item => item.id === segment.id);
  const hasNextSegment =
    currentIndex !== -1 && currentIndex < filteredItems.length - 1;

  // #2250 — buttons were h-7 (28px). Four stacked in the 92px row
  // (Plus + Split + kebab + Comment) overflowed the row and the last
  // one got clipped by `overflow-y-hidden`. Dropping to h-5 (20px)
  // fits: 4 × 20 = 80px inside the 92px row.
  const iconButton = cn(
    'inline-flex h-5 w-5 items-center justify-center rounded-full border transition-colors',
    'hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
    'cursor-pointer'
  );

  const menuItem = cn(
    'flex w-full items-center gap-2 px-3 py-1.5 text-xs cursor-pointer',
    'data-[focus]:opacity-80 data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed'
  );

  const iconButtonStyle = {
    backgroundColor: getColour('secondLayer'),
    borderColor: getColour('border'),
    color: getColour('icon')
  };

  // #2250 — the comment compose trigger sits in the same stack as the
  // count-changer tools so the visibility rule stays uniform.
  // Translation rows hide the count-changers but keep this button —
  // you can still comment on a translation segment.
  const commentsCtx = useCommentsContext();
  const handleComposeComment = () => {
    if (!commentsCtx) return;
    commentsCtx.startDraft({
      indexFrom: String(segment.start),
      indexTo: String(segment.stop)
    });
  };

  return (
    <>
      <div className="flex flex-col items-end gap-0.5 py-0.5 pr-4">
        {!hideCountTools && (
          <>
            <Tooltip
              label={shortcutLabel(t('add'), 'Ctrl+Enter')}
              placement="left"
            >
              <button
                type="button"
                onClick={() => addListItem(segment.id)}
                aria-label={t('add')}
                className={iconButton}
                style={iconButtonStyle}
              >
                <Plus size={11} aria-hidden="true" />
              </button>
            </Tooltip>

            <Tooltip
              label={shortcutLabel(t('split'), 'Ctrl+Shift+X')}
              placement="left"
            >
              <button
                type="button"
                onClick={() => splitListItem(segment.id)}
                aria-label={t('split')}
                className={iconButton}
                style={iconButtonStyle}
              >
                <Scissors size={11} aria-hidden="true" />
              </button>
            </Tooltip>

            <Menu as="div" className="relative">
              <Tooltip label={t('more-actions')} placement="left">
                <MenuButton
                  aria-label={t('more-actions')}
                  className={iconButton}
                  style={iconButtonStyle}
                >
                  <MoreHorizontal size={11} aria-hidden="true" />
                </MenuButton>
              </Tooltip>
              <MenuItems
                anchor="bottom end"
                className={cn(
                  'min-w-52 rounded-2xl border shadow-lg overflow-hidden py-1 z-50',
                  'focus:outline-none'
                )}
                style={{
                  backgroundColor: getColour('secondLayer'),
                  borderColor: getColour('border')
                }}
              >
                <MenuItem
                  as="button"
                  disabled={!hasNextSegment}
                  onClick={() => joinListItems(segment.id)}
                  className={menuItem}
                  style={{ color: getColour('normalText') }}
                >
                  <Merge size={13} aria-hidden="true" />
                  <span>
                    {hasNextSegment
                      ? t('merge-with-next')
                      : t('no-next-segment')}
                  </span>
                </MenuItem>
                <MenuItem
                  as="button"
                  onClick={() => removeListItem(segment.id)}
                  className={menuItem}
                  style={{ color: 'rgb(220 38 38)' }}
                >
                  <Trash2 size={14} aria-hidden="true" />
                  <span>
                    {shortcutLabel(t('remove'), 'Ctrl+Shift+Backspace')}
                  </span>
                </MenuItem>
                <MenuItem
                  as="button"
                  onClick={() => openTrainingDialog(segment.id)}
                  className={menuItem}
                  style={{ color: getColour('normalText') }}
                >
                  <BrainCircuit size={13} aria-hidden="true" />
                  <span>{t('add-to-training-data')}</span>
                </MenuItem>

                {/* No speaker separation in the source: explain it instead of
                    leaving the menu silently short. */}
                {voiceRow.canAssign &&
                  voiceRow.unavailableReason === 'no-diarization' && (
                    <MenuItem
                      as="button"
                      disabled
                      className={menuItem}
                      style={{ color: getColour('inactiveText') }}
                    >
                      <Fingerprint size={13} aria-hidden="true" />
                      <span>{t('voice-error-no-diarization')}</span>
                    </MenuItem>
                  )}

                {voiceRow.canAssign && !voiceRow.unavailableReason && (
                  <MenuItem
                    as="button"
                    onClick={() => voiceRow.openAssign()}
                    className={menuItem}
                    style={{ color: getColour('normalText') }}
                  >
                    <Fingerprint size={13} aria-hidden="true" />
                    <span>{t('voice-assign-person')}</span>
                  </MenuItem>
                )}

                {voiceRow.canEnroll && !voiceRow.unavailableReason && (
                  <MenuItem
                    as="button"
                    onClick={voiceRow.openEnroll}
                    className={menuItem}
                    style={{ color: getColour('normalText') }}
                  >
                    <Mic size={13} aria-hidden="true" />
                    <span>{t('voice-save-sample')}</span>
                  </MenuItem>
                )}
              </MenuItems>
            </Menu>
          </>
        )}

        <Tooltip label={t('add-comment') || 'Add comment'} placement="left">
          <button
            type="button"
            onClick={handleComposeComment}
            aria-label={t('add-comment') || 'Add comment'}
            className={iconButton}
            style={iconButtonStyle}
            disabled={!commentsCtx}
            title={
              !commentsCtx
                ? 'Comments unavailable — CommentsProvider not mounted'
                : undefined
            }
          >
            <MessageCirclePlus size={11} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>

      {voiceRow.dialogs}
    </>
  );
};
